import React from 'react';
import { Phone, PhoneCall, PhoneIncoming, PhoneOff, PhoneOutgoing, RefreshCw, X } from 'lucide-react';
import { Invitation, Inviter, Registerer, Session, SessionState, UserAgent } from 'sip.js';
import { Role, UserAccount } from '../types';
import { sPhoneService, SPhoneMyConfig } from '../services/sPhoneService';

type Props = {
  currentUser: UserAccount | null;
  currentRole: Role;
  activeTab?: string;
};

type SoftPhoneState =
  | 'DISABLED'
  | 'READY'
  | 'CONNECTING'
  | 'REGISTERED'
  | 'INCOMING'
  | 'CALLING'
  | 'IN_CALL'
  | 'ERROR';

const normalizeDial = (value: string): string => String(value || '').replace(/[^0-9+*#@._-]/g, '').trim();

export const GlobalSoftPhoneDrawer: React.FC<Props> = ({ currentUser }) => {
  const currentUserId = String(currentUser?.id || '').trim();
  const [open, setOpen] = React.useState(false);
  const [config, setConfig] = React.useState<SPhoneMyConfig | null>(null);
  const [busyConfig, setBusyConfig] = React.useState(false);
  const [error, setError] = React.useState('');
  const [phoneState, setPhoneState] = React.useState<SoftPhoneState>('READY');
  const [dialTarget, setDialTarget] = React.useState('');
  const [muted, setMuted] = React.useState(false);
  const [callLabel, setCallLabel] = React.useState('');

  const remoteAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const userAgentRef = React.useRef<UserAgent | null>(null);
  const registererRef = React.useRef<Registerer | null>(null);
  const sessionRef = React.useRef<Session | null>(null);
  const incomingRef = React.useRef<Invitation | null>(null);

  const fetchConfig = React.useCallback(async () => {
    if (!currentUserId) return;
    try {
      setBusyConfig(true);
      setError('');
      const next = await sPhoneService.getMyConfig();
      setConfig(next);
      if (!next?.enabled) {
        setPhoneState('DISABLED');
      } else if (!userAgentRef.current) {
        setPhoneState('READY');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load S.PHONE config.');
      setPhoneState('ERROR');
      setConfig({ enabled: false, reason: 'Config fetch failed' });
    } finally {
      setBusyConfig(false);
    }
  }, [currentUserId]);

  React.useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  React.useEffect(() => {
    if (!open) return;
    void fetchConfig();
  }, [fetchConfig, open]);

  const teardown = React.useCallback(async () => {
    try {
      const session = sessionRef.current as any;
      if (session) {
        if (session.state === SessionState.Established) {
          await session.bye();
        } else if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
          if (typeof session.cancel === 'function') await session.cancel();
        }
      }
    } catch {
      // ignore
    }
    try {
      if (registererRef.current) await registererRef.current.unregister();
    } catch {
      // ignore
    }
    try {
      if (userAgentRef.current) await userAgentRef.current.stop();
    } catch {
      // ignore
    }
    sessionRef.current = null;
    incomingRef.current = null;
    registererRef.current = null;
    userAgentRef.current = null;
    setMuted(false);
    setCallLabel('');
    setPhoneState(config?.enabled ? 'READY' : 'DISABLED');
  }, [config?.enabled]);

  React.useEffect(() => {
    return () => {
      void teardown();
    };
  }, [teardown]);

  const applyRemoteAudio = React.useCallback((session: Session) => {
    const sdh = (session as any)?.sessionDescriptionHandler;
    const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
    if (!pc) return;
    pc.ontrack = (event) => {
      const stream = event.streams?.[0];
      const audio = remoteAudioRef.current;
      if (!audio || !stream) return;
      audio.srcObject = stream;
      void audio.play().catch(() => undefined);
    };
  }, []);

  const bindSession = React.useCallback((session: Session, label: string) => {
    sessionRef.current = session;
    setCallLabel(label);
    applyRemoteAudio(session);
    session.stateChange.addListener((state) => {
      if (state === SessionState.Establishing) {
        setPhoneState('CALLING');
      } else if (state === SessionState.Established) {
        setPhoneState('IN_CALL');
      } else if (state === SessionState.Terminated) {
        sessionRef.current = null;
        incomingRef.current = null;
        setMuted(false);
        setCallLabel('');
        setPhoneState(config?.enabled ? 'REGISTERED' : 'DISABLED');
      }
    });
  }, [applyRemoteAudio, config?.enabled]);

  const connect = React.useCallback(async () => {
    if (userAgentRef.current) return;
    const account = config?.account;
    if (!config?.enabled || !account?.enabled) {
      setError(config?.reason || 'S.PHONE is disabled for your account.');
      setPhoneState('DISABLED');
      return;
    }
    if (!config.wsServer || !config.domain || !account.username || !account.password) {
      setError('S.PHONE account is incomplete. Ask admin to configure wsServer/domain/credentials.');
      setPhoneState('ERROR');
      return;
    }

    try {
      setError('');
      setPhoneState('CONNECTING');
      const uri = UserAgent.makeURI(`sip:${encodeURIComponent(account.username)}@${config.domain}`);
      if (!uri) throw new Error('Invalid SIP URI.');

      const iceUrls = Array.isArray(config.iceServers) ? config.iceServers.filter(Boolean) : [];
      const iceServers = iceUrls.length
        ? iceUrls.map((url) => ({ urls: url }))
        : [{ urls: 'stun:stun.l.google.com:19302' }];

      const userAgent = new UserAgent({
        uri,
        displayName: String(account.displayName || currentUser?.name || account.extension || account.username),
        authorizationUsername: account.username,
        authorizationPassword: account.password,
        transportOptions: { server: config.wsServer },
        sessionDescriptionHandlerFactoryOptions: {
          constraints: { audio: true, video: false },
          peerConnectionConfiguration: { iceServers },
        },
      });

      userAgent.delegate = {
        onInvite: (invitation: Invitation) => {
          incomingRef.current = invitation;
          bindSession(invitation, String(invitation.remoteIdentity?.uri?.user || 'Incoming call'));
          setPhoneState('INCOMING');
          setOpen(true);
        },
      };

      await userAgent.start();
      const registerer = new Registerer(userAgent);
      await registerer.register();

      userAgentRef.current = userAgent;
      registererRef.current = registerer;
      setPhoneState('REGISTERED');
    } catch (e: any) {
      setError(e?.message || 'Failed to connect to PBX.');
      await teardown();
      setPhoneState('ERROR');
    }
  }, [bindSession, config, currentUser?.name, teardown]);

  React.useEffect(() => {
    if (!open) return;
    if (!config?.enabled) return;
    if (config?.autoRegister === false) return;
    if (userAgentRef.current) return;
    void connect();
  }, [config?.autoRegister, config?.enabled, connect, open]);

  const disconnect = React.useCallback(async () => {
    await teardown();
  }, [teardown]);

  const call = React.useCallback(async () => {
    const account = config?.account;
    if (!userAgentRef.current || !config?.domain || !account) {
      setError('Connect S.PHONE first.');
      return;
    }
    const cleaned = normalizeDial(dialTarget);
    if (!cleaned) return;

    const onlyDigits = /^\+?\d+$/.test(cleaned);
    const looksExternal = onlyDigits && cleaned.replace(/\D/g, '').length > 4;
    const looksInternal = onlyDigits && cleaned.replace(/\D/g, '').length <= 4;
    if (looksExternal && account.allowExternalCalls === false) {
      setError('External calling is disabled for your account.');
      return;
    }
    if (looksInternal && account.allowInternalCalls === false) {
      setError('Internal calling is disabled for your account.');
      return;
    }

    let normalized = cleaned;
    if (looksExternal && config.defaultOutboundPrefix) {
      normalized = `${config.defaultOutboundPrefix}${cleaned}`;
    }
    const targetUri = cleaned.startsWith('sip:') ? cleaned : `sip:${normalized}@${config.domain}`;
    const uri = UserAgent.makeURI(targetUri);
    if (!uri) {
      setError('Invalid dial target.');
      return;
    }

    try {
      setError('');
      setPhoneState('CALLING');
      const inviter = new Inviter(userAgentRef.current, uri, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      bindSession(inviter, cleaned);
      await inviter.invite();
    } catch (e: any) {
      setError(e?.message || 'Failed to place call.');
      setPhoneState('REGISTERED');
    }
  }, [bindSession, config, dialTarget]);

  const hangup = React.useCallback(async () => {
    const session = sessionRef.current as any;
    if (!session) return;
    try {
      if (session.state === SessionState.Established) {
        await session.bye();
      } else if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
        if (typeof session.cancel === 'function') await session.cancel();
      }
    } catch {
      // ignore
    }
  }, []);

  const acceptIncoming = React.useCallback(async () => {
    const invitation = incomingRef.current;
    if (!invitation) return;
    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      setPhoneState('IN_CALL');
    } catch (e: any) {
      setError(e?.message || 'Failed to accept incoming call.');
    }
  }, []);

  const rejectIncoming = React.useCallback(async () => {
    const invitation = incomingRef.current;
    if (!invitation) return;
    try {
      await invitation.reject();
      incomingRef.current = null;
      setPhoneState('REGISTERED');
    } catch {
      // ignore
    }
  }, []);

  const toggleMute = React.useCallback(() => {
    const session = sessionRef.current as any;
    const pc: RTCPeerConnection | undefined = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    const nextMuted = !muted;
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !nextMuted;
    });
    setMuted(nextMuted);
  }, [muted]);

  if (!currentUserId) return null;

  const statusTone =
    phoneState === 'REGISTERED' || phoneState === 'IN_CALL'
      ? 'bg-emerald-100 text-emerald-700'
      : phoneState === 'CONNECTING' || phoneState === 'CALLING' || phoneState === 'INCOMING'
        ? 'bg-amber-100 text-amber-700'
        : phoneState === 'ERROR'
          ? 'bg-rose-100 text-rose-700'
          : 'bg-slate-100 text-slate-600';

  return (
    <>
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-24 right-6 z-[130] inline-flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-900/70 bg-slate-900 text-white shadow-[0_16px_34px_rgba(2,8,23,0.45)] transition hover:scale-[1.02]"
        aria-label="Open S.PHONE softphone"
      >
        <Phone className="h-6 w-6" />
      </button>

      {open ? (
        <div className="fixed bottom-24 right-24 z-[125] w-[360px] rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_30px_70px_rgba(2,8,23,0.3)]">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">S.PHONE</div>
              <div className="text-sm font-bold text-slate-800">Browser Softphone</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 text-slate-500 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-3 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${statusTone}`}>{phoneState}</span>
            <button
              type="button"
              onClick={() => void fetchConfig()}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-slate-600"
            >
              <RefreshCw className={`h-3 w-3 ${busyConfig ? 'animate-spin' : ''}`} />
              Sync
            </button>
            {userAgentRef.current ? (
              <button
                type="button"
                onClick={() => void disconnect()}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-rose-700"
              >
                <PhoneOff className="h-3 w-3" />
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void connect()}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-700"
              >
                <PhoneCall className="h-3 w-3" />
                Connect
              </button>
            )}
          </div>

          <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">Dial</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={dialTarget}
              onChange={(event) => setDialTarget(normalizeDial(event.target.value))}
              placeholder="Extension or phone number"
              className="h-10 flex-1 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-400"
            />
            <button
              type="button"
              onClick={() => void call()}
              disabled={!dialTarget || !userAgentRef.current || phoneState === 'IN_CALL' || phoneState === 'CALLING'}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white disabled:opacity-40"
            >
              <PhoneOutgoing className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => void hangup()}
              disabled={!sessionRef.current}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-rose-600 text-white disabled:opacity-40"
            >
              <PhoneOff className="h-4 w-4" />
            </button>
          </div>

          {phoneState === 'INCOMING' ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-black uppercase tracking-[0.12em] text-amber-700">Incoming Call</div>
              <div className="mt-1 text-sm font-semibold text-slate-800">{callLabel || 'Unknown caller'}</div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void acceptIncoming()}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-700"
                >
                  <PhoneIncoming className="h-3 w-3" />
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => void rejectIncoming()}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-rose-700"
                >
                  <PhoneOff className="h-3 w-3" />
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {phoneState === 'IN_CALL' ? (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-xs font-black uppercase tracking-[0.12em] text-emerald-700">In Call</div>
              <div className="mt-1 text-sm font-semibold text-slate-800">{callLabel || 'Connected'}</div>
              <button
                type="button"
                onClick={toggleMute}
                className="mt-2 inline-flex rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-slate-700"
              >
                {muted ? 'Unmute' : 'Mute'}
              </button>
            </div>
          ) : null}

          {error ? <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
          {!config?.enabled ? <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">{config?.reason || 'S.PHONE is disabled.'}</div> : null}
        </div>
      ) : null}
    </>
  );
};

export default GlobalSoftPhoneDrawer;
